import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  AlertTriangle, Shield, ShieldCheck, ShieldX, ArrowUpRight, Filter,
  ChevronDown, ChevronRight, Clock, Target, TrendingUp, Search,
  Check, X, AlertCircle, Download
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dqp_token')}` } });

const SEV_COLORS = {
  critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-100 text-red-700' },
  high: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700' },
  low: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700' },
};

const STATUS_COLORS = {
  detected: 'bg-gray-100 text-gray-700',
  under_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div data-testid={`stat-${label.toLowerCase()}`} className={`${color} rounded-xl px-4 py-3 border`}>
      <div className="flex items-center justify-between">
        <span className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>{value}</span>
        <Icon size={18} className="opacity-60" />
      </div>
      <span className="text-xs font-medium mt-1 block opacity-80">{label}</span>
    </div>
  );
}

function ConfidenceBar({ confidence }) {
  const pct = Math.round(confidence * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-[#e6e8f2] rounded-full overflow-hidden">
        <div className="h-full rounded-full bg-[#5b63ea] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-semibold text-[#5b63ea]">{pct}%</span>
    </div>
  );
}

export default function Signals() {
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState({ status: '', severity: '', type: '' });
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [showRegistry, setShowRegistry] = useState(false);
  const [registry, setRegistry] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const loadSignals = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.set('status', filter.status);
      if (filter.severity) params.set('severity', filter.severity);
      if (filter.type) params.set('signal_type', filter.type);
      const { data } = await axios.get(`${API}/signals?${params}`, authH());
      setSignals(data);
    } catch {}
  }, [filter]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/signals/stats`, authH());
      setStats(data);
    } catch {}
  }, []);

  const loadRegistry = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/signals/registry`, authH());
      setRegistry(data);
    } catch {}
  }, []);

  useEffect(() => { loadSignals(); }, [loadSignals]);
  useEffect(() => { loadStats(); loadRegistry(); }, [loadStats, loadRegistry]);

  const handleReview = async (action) => {
    if (!selectedSignal) return;
    try {
      await axios.post(`${API}/signals/${selectedSignal.id}/review`, {
        action, notes: reviewNotes
      }, authH());
      toast.success(`Insight ${action}d successfully`);
      setSelectedSignal(null);
      setReviewNotes('');
      loadSignals();
      loadStats();
      loadRegistry();
    } catch { toast.error('Review failed'); }
  };

  const filteredSignals = signals.filter(s =>
    !searchTerm || s.name.toLowerCase().includes(searchTerm.toLowerCase()) || s.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExportRegistry = () => {
    const header = 'Name,Severity,Type,Confidence,Metric,Baseline,Approved By,Approved Date';
    const rows = registry.map(r =>
      `"${r.name}","${r.severity}","${r.type}",${r.confidence},"${r.metric_value}","${r.baseline_value}","${r.approved_by}","${r.approved_date}"`
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'insight_registry.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success('Registry exported');
  };

  return (
    <DashboardLayout>
      <div data-testid="signals-page" className="p-6 space-y-6">
        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard label="Total" value={stats.total || 0} color="bg-[#f5f6fb] border-[#e6e8f2] text-[#1e1e2e]" icon={AlertTriangle} />
          <StatCard label="Critical" value={stats.critical || 0} color={`${SEV_COLORS.critical.bg} ${SEV_COLORS.critical.border} ${SEV_COLORS.critical.text}`} icon={AlertCircle} />
          <StatCard label="High" value={stats.high || 0} color={`${SEV_COLORS.high.bg} ${SEV_COLORS.high.border} ${SEV_COLORS.high.text}`} icon={AlertTriangle} />
          <StatCard label="Medium" value={stats.medium || 0} color={`${SEV_COLORS.medium.bg} ${SEV_COLORS.medium.border} ${SEV_COLORS.medium.text}`} icon={Target} />
          <StatCard label="Low" value={stats.low || 0} color={`${SEV_COLORS.low.bg} ${SEV_COLORS.low.border} ${SEV_COLORS.low.text}`} icon={Shield} />
        </div>

        {/* Tab Toggle + Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-1 bg-white border border-[#e6e8f2] rounded-lg p-0.5">
            <button data-testid="tab-all-signals" onClick={() => setShowRegistry(false)} className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${!showRegistry ? 'bg-[#5b63ea] text-white' : 'text-[#6b7280] hover:bg-[#f5f6fb]'}`}>
              All Insights
            </button>
            <button data-testid="tab-registry" onClick={() => setShowRegistry(true)} className={`px-3 py-1.5 rounded text-sm font-medium transition-all flex items-center gap-1.5 ${showRegistry ? 'bg-[#5b63ea] text-white' : 'text-[#6b7280] hover:bg-[#f5f6fb]'}`}>
              <ShieldCheck size={14} /> Registry ({registry.length})
            </button>
          </div>
          {!showRegistry && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 bg-white border border-[#e6e8f2] rounded-lg px-3 py-1.5">
                <Search size={13} className="text-[#9ca3af]" />
                <input data-testid="signal-search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search insights..." className="text-sm bg-transparent outline-none w-36" />
              </div>
              <select data-testid="filter-status" value={filter.status} onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))} className="text-xs bg-white border border-[#e6e8f2] rounded-lg px-2 py-1.5 outline-none">
                <option value="">All Status</option>
                <option value="detected">Detected</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <select data-testid="filter-severity" value={filter.severity} onChange={(e) => setFilter(f => ({ ...f, severity: e.target.value }))} className="text-xs bg-white border border-[#e6e8f2] rounded-lg px-2 py-1.5 outline-none">
                <option value="">All Severity</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <select data-testid="filter-type" value={filter.type} onChange={(e) => setFilter(f => ({ ...f, type: e.target.value }))} className="text-xs bg-white border border-[#e6e8f2] rounded-lg px-2 py-1.5 outline-none">
                <option value="">All Types</option>
                <option value="threshold_breach">Threshold Breach</option>
                <option value="distribution_drift">Distribution Drift</option>
                <option value="volume_shift">Volume Shift</option>
                <option value="relationship_violation">Relationship Violation</option>
              </select>
            </div>
          )}
        </div>

        {/* Registry View */}
        {showRegistry ? (
          <div className="bg-white border border-[#e6e8f2] rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#e6e8f2] bg-[#f8f9fc]">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-emerald-600" />
                <span className="text-sm font-semibold text-[#1e1e2e]">Approved Insight Registry</span>
              </div>
              {registry.length > 0 && (
                <Button variant="ghost" size="sm" onClick={handleExportRegistry} className="h-7 text-xs gap-1" data-testid="export-registry-btn">
                  <Download size={12} /> Export CSV
                </Button>
              )}
            </div>
            {registry.length === 0 ? (
              <div className="py-12 text-center text-sm text-[#9ca3af]">No approved insights yet. Review insights to add them to the registry.</div>
            ) : (
              <div className="divide-y divide-[#e6e8f2]">
                {registry.map(sig => (
                  <div key={sig.id} data-testid={`registry-${sig.id}`} className="px-5 py-3 flex items-center gap-4">
                    <ShieldCheck size={16} className="text-emerald-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1e1e2e] truncate">{sig.name}</p>
                      <p className="text-[10px] text-[#9ca3af]">Approved by {sig.approved_by} on {sig.approved_date?.split('T')[0]}</p>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${SEV_COLORS[sig.severity]?.badge}`}>{sig.severity}</span>
                    <span className="text-xs text-[#6b7280]">{sig.metric_value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Insights List */
          <div className="space-y-3">
            {filteredSignals.length === 0 ? (
              <div className="bg-white border border-[#e6e8f2] rounded-xl py-12 text-center text-sm text-[#9ca3af]">
                {signals.length === 0 ? 'No insights detected yet. Connect a data source to generate insights.' : 'No insights match your filters.'}
              </div>
            ) : (
              filteredSignals.map(sig => (
                <div key={sig.id} data-testid={`signal-card-${sig.id}`}
                  className={`bg-white border rounded-xl p-4 hover:shadow-md transition-all cursor-pointer ${SEV_COLORS[sig.severity]?.border || 'border-[#e6e8f2]'}`}
                  onClick={() => setSelectedSignal(sig)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${SEV_COLORS[sig.severity]?.bg}`}>
                      <AlertTriangle size={16} className={SEV_COLORS[sig.severity]?.text} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-[#1e1e2e]">{sig.name}</h3>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${SEV_COLORS[sig.severity]?.badge}`}>{sig.severity}</span>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[sig.status]}`}>{sig.status.replace('_', ' ')}</span>
                        <span className="text-[10px] bg-[#f5f6fb] text-[#6b7280] px-2 py-0.5 rounded-full">{sig.type.replace('_', ' ')}</span>
                      </div>
                      <p className="text-xs text-[#6b7280] mt-1 line-clamp-2">{sig.description}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="w-24"><ConfidenceBar confidence={sig.confidence} /></div>
                        <span className="text-[10px] text-[#9ca3af] flex items-center gap-1"><Clock size={10} />{sig.detection_date}</span>
                        <div className="flex gap-1">
                          {sig.affected_tables?.map(t => (
                            <span key={t} className="text-[10px] bg-[#5b63ea]/10 text-[#5b63ea] px-1.5 py-0.5 rounded">{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>{sig.metric_value}</p>
                      <p className="text-[10px] text-[#9ca3af]">vs {sig.baseline_value}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Insight Detail Dialog */}
        <Dialog open={!!selectedSignal} onOpenChange={(open) => { if (!open) setSelectedSignal(null); }}>
          <DialogContent className="scrollbar-light bg-white border-[#e6e8f2] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="signal-detail-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#1e1e2e] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <AlertTriangle size={18} className={SEV_COLORS[selectedSignal?.severity]?.text} />
                {selectedSignal?.name}
              </DialogTitle>
              <DialogDescription className="text-sm text-[#6b7280]">{selectedSignal?.description}</DialogDescription>
            </DialogHeader>

            {selectedSignal && (
              <div className="space-y-4 mt-2">
                {/* Metric vs Baseline */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-red-400 font-semibold">Current Value</p>
                    <p className="text-2xl font-bold text-red-700 mt-1" style={{ fontFamily: 'Outfit' }}>{selectedSignal.metric_value}</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold">Baseline</p>
                    <p className="text-2xl font-bold text-emerald-700 mt-1" style={{ fontFamily: 'Outfit' }}>{selectedSignal.baseline_value}</p>
                  </div>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <p className="text-[10px] text-[#9ca3af] uppercase tracking-wider">Severity</p>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mt-1 inline-block ${SEV_COLORS[selectedSignal.severity]?.badge}`}>{selectedSignal.severity}</span>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#9ca3af] uppercase tracking-wider">Confidence</p>
                    <div className="mt-1 w-20"><ConfidenceBar confidence={selectedSignal.confidence} /></div>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#9ca3af] uppercase tracking-wider">Type</p>
                    <p className="text-xs text-[#1e1e2e] mt-1 capitalize">{selectedSignal.type.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#9ca3af] uppercase tracking-wider">Detected</p>
                    <p className="text-xs text-[#1e1e2e] mt-1">{selectedSignal.detection_date}</p>
                  </div>
                </div>

                {/* Affected tables/columns */}
                <div>
                  <p className="text-[10px] text-[#9ca3af] uppercase tracking-wider mb-1">Affected Tables</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedSignal.affected_tables?.map(t => (
                      <span key={t} className="text-xs bg-[#5b63ea]/10 text-[#5b63ea] px-2 py-0.5 rounded">{t}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] text-[#9ca3af] uppercase tracking-wider mb-1">Affected Columns</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedSignal.affected_columns?.map(c => (
                      <span key={c} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{c}</span>
                    ))}
                  </div>
                </div>

                {/* Statistical Details */}
                {selectedSignal.details && (
                  <div className="bg-[#f8f9fc] border border-[#e6e8f2] rounded-lg p-3 space-y-1">
                    <p className="text-[10px] text-[#9ca3af] uppercase tracking-wider mb-1">Statistical Details</p>
                    {selectedSignal.details.trend && <p className="text-xs text-[#4b5563]">Trend: <strong>{selectedSignal.details.trend}</strong></p>}
                    {selectedSignal.details.affected_period && <p className="text-xs text-[#4b5563]">Period: {selectedSignal.details.affected_period}</p>}
                    {selectedSignal.details.sample_size && <p className="text-xs text-[#4b5563]">Sample Size: {selectedSignal.details.sample_size}</p>}
                    {selectedSignal.details.statistical_test && <p className="text-xs text-[#4b5563]">Test: {selectedSignal.details.statistical_test}</p>}
                  </div>
                )}

                {/* Audit Trail */}
                {selectedSignal.review_history?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-[#9ca3af] uppercase tracking-wider mb-2">Audit Trail</p>
                    <div className="space-y-2">
                      {selectedSignal.review_history.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${r.action === 'approve' ? 'bg-emerald-100 text-emerald-600' : r.action === 'reject' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                            {r.action === 'approve' ? <Check size={10} /> : r.action === 'reject' ? <X size={10} /> : <ArrowUpRight size={10} />}
                          </div>
                          <div>
                            <span className="font-medium text-[#1e1e2e] capitalize">{r.action}d</span> by {r.reviewer_name} <span className="text-[#9ca3af]">on {r.timestamp?.split('T')[0]}</span>
                            {r.notes && <p className="text-[#6b7280] mt-0.5 italic">"{r.notes}"</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Review Actions */}
                {selectedSignal.status !== 'approved' && selectedSignal.status !== 'rejected' && (
                  <div className="border-t border-[#e6e8f2] pt-4">
                    <p className="text-[10px] text-[#9ca3af] uppercase tracking-wider mb-2">Review Actions</p>
                    <textarea
                      data-testid="review-notes"
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Add review notes (optional)..."
                      className="w-full border border-[#e6e8f2] rounded-lg p-2 text-sm text-[#1e1e2e] placeholder:text-[#9ca3af] outline-none focus:ring-1 focus:ring-[#5b63ea] resize-none h-16"
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <Button data-testid="btn-approve" onClick={() => handleReview('approve')} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 h-8 text-xs">
                        <Check size={12} /> Approve
                      </Button>
                      <Button data-testid="btn-reject" onClick={() => handleReview('reject')} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 gap-1 h-8 text-xs">
                        <X size={12} /> Reject
                      </Button>
                      <Button data-testid="btn-escalate" onClick={() => handleReview('escalate')} variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50 gap-1 h-8 text-xs">
                        <ArrowUpRight size={12} /> Escalate
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
